import React from 'react'
import { Box, Stack, Typography } from '@mui/material'
import { macShortcuts, windowsShortcuts } from '../KeyboardShortcuts/ShortcutsKeymap'
import MacShift from '../../assets/icons/MacShift'
import MacCommand from '../../assets/icons/MacCommand'
import MacOption from '../../assets/icons/MacOption'
import ArrowRightKeyboard from '../../assets/icons/ArrowRightKeyboard'
import ArrowLeftKeyboard from '../../assets/icons/ArrowLeftKeyboard'
import ArrowDownKeyboard from '../../assets/icons/ArrowDownKeyboard'
import ArrowUpKeyboard from '../../assets/icons/ArrowUpKeyboard'
import { isMacOS } from '../../util/general'

type ShortcutKeyMap = Record<string, string>
type ShortcutsCategories = Record<string, ShortcutKeyMap>

/** Keys of a named shortcut for the current OS, or null when it has no binding here. */
export function getShortcutKeys(shortcutName: string): string[] | null {
    const shortcuts: ShortcutsCategories = isMacOS() ? macShortcuts : windowsShortcuts
    for (const category in shortcuts) {
        const binding = shortcuts[category][shortcutName]
        if (binding) return binding.split('+')
    }
    return null
}

const MAC_GLYPHS: Record<string, React.ReactNode> = {
    Shift: <MacShift />,
    Meta: <MacCommand />,
    Alt: <MacOption />,
    ArrowUp: <ArrowUpKeyboard />,
    ArrowDown: <ArrowDownKeyboard />,
    ArrowLeft: <ArrowLeftKeyboard />,
    ArrowRight: <ArrowRightKeyboard />
}

const KeyBox: React.FC<{ keyLabel: string; compact: boolean }> = ({ keyLabel, compact }) => {
    const glyph = isMacOS() ? MAC_GLYPHS[keyLabel] : undefined
    const size = compact ? 22 : 32
    return (
        <Box
            sx={{
                minWidth: size,
                height: size,
                px: compact ? 0.75 : 1,
                borderRadius: compact ? 1 : '6px',
                border: (theme) => `1px solid ${theme.palette.divider}`,
                boxShadow: (theme) => `0 ${compact ? 1 : 2}px 0 ${theme.palette.divider}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxSizing: 'border-box',
                color: 'text.secondary',
                fontSize: compact ? 11 : 14,
                fontVariantNumeric: 'tabular-nums',
                '& svg': { fontSize: compact ? 14 : 20 }
            }}
        >
            {glyph ?? <Typography component="span" sx={{ fontSize: 'inherit', color: 'inherit', lineHeight: 1 }}>{keyLabel}</Typography>}
        </Box>
    )
}

type Props = {
    shortcutName: string
    /** 22px keys for dense lists (sidebar). */
    compact?: boolean
}

const ShortcutRenderer: React.FC<Props> = ({ shortcutName, compact = false }) => {
    const keys = getShortcutKeys(shortcutName)
    if (!keys) return null
    return (
        <Stack direction="row" spacing={compact ? 0.5 : 1} alignItems="center" sx={{ flexShrink: 0 }}>
            {keys.map((key, index) => (
                <Stack key={`${key}-${index}`} spacing={compact ? 0.5 : 1} direction="row" alignItems="center">
                    <KeyBox keyLabel={key} compact={compact} />
                    {index < keys.length - 1 ? (
                        <Typography component="span" color="text.tertiary" sx={{ fontSize: compact ? 11 : 16, lineHeight: 1 }}>
                            +
                        </Typography>
                    ) : null}
                </Stack>
            ))}
        </Stack>
    )
}

export default ShortcutRenderer
